/**
 * 功能开关 Hook
 *
 * 调用 /api/config/features 获取功能开关状态，缓存到 localStorage。
 * 管理员始终可访问所有功能（isAdmin=true 时所有开关视为 true）。
 */

import { useState, useEffect, useCallback } from 'react';
import { api, type PublicFeatureFlags } from '../services/api';

const CACHE_KEY = 'feature_flags';
const CACHE_TTL = 5 * 60 * 1000; // 5 分钟缓存

interface CachedFlags {
  flags: PublicFeatureFlags;
  ts: number;
}

const DEFAULT_FLAGS: PublicFeatureFlags = {
  directorEnabled: true,
  canvasEnabled: true,
  ecommerceReplicateEnabled: true,
  ecommerceSuiteEnabled: true,
  pptEnabled: true,
  videoEnabled: true,
  photoRestoreEnabled: true,
  isAdmin: false,
};

function loadCachedFlags(): PublicFeatureFlags | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedFlags = JSON.parse(raw);
    if (Date.now() - cached.ts > CACHE_TTL) return null;
    return cached.flags;
  } catch {
    return null;
  }
}

function saveCachedFlags(flags: PublicFeatureFlags) {
  try {
    const cached: CachedFlags = { flags, ts: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch { /* ignore */ }
}

export function clearFeatureFlagsCache() {
  localStorage.removeItem(CACHE_KEY);
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState<PublicFeatureFlags>(() => loadCachedFlags() || DEFAULT_FLAGS);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await api.getFeatureFlags();
      setFlags(data);
      saveCachedFlags(data);
    } catch {
      // 获取失败时保持默认全开（不影响用户使用）
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 管理员始终可访问所有功能
  const isAdmin = flags.isAdmin;

  return {
    /** 原始开关值 */
    flags,
    /** 是否已加载完成 */
    loaded,
    /** 手动刷新 */
    refresh,
    /** 便捷判定：某功能对当前用户是否可见 */
    isEnabled: (feature: keyof Omit<PublicFeatureFlags, 'isAdmin'>): boolean => {
      if (isAdmin) return true;
      return flags[feature] !== false;
    },
  };
}
